import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './styles.module.css';
import Text from 'components/shared/Text';
import MobileContext from 'context/MobileContext';
import { Formik } from 'formik';
import { contactUsSchema } from 'validation/auth.validation';
import Textfield from 'components/shared/Textfield';
import TextArea from 'components/shared/TextArea';
import CustomDropdown from 'components/shared/SelectState';
import { Dialog, makeStyles } from '@material-ui/core';
import { checkmarkCircleOutline, infoIcon } from 'assets/newIcons';
import { CircularProgress } from '@mui/material';
import history from 'store/history';

const initialValues = {
  email: '',
  subject: '',
  location: '',
  message: '',
};

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: '12px',
  },
}));

const ContactUs = ({
  loadDropdownData,
  dropdownDetails,
  submitContact,
  isSubmitContactLoading,
  contactUsDetails,
}) => {
  const isMobile = useContext(MobileContext);
  const userDetails = useSelector(state => state?.auth?.profile);
  const userFirstName = useSelector(state => state?.auth?.user?.firstName);
  const userPhoneNumber = userDetails?.phone || userDetails?.mobilephone;
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openThanksModal, setOpenThanksModal] = useState(false);
  const [onContactUs, setOnContactUs] = useState(false);
  const [subject, setSubject] = useState('');
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const validData = subject && location && message.trim();
  const classes = useStyles();
  const boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.08)';
  const inputWidth = isMobile ? '90.41vw' : '59.5vw';
  const subjectOptions = dropdownDetails?.dropDown2Data;
  const locationOptions = dropdownDetails?.dropDown1Data;

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    if (onContactUs && contactUsDetails?.success) {
      setOpenThanksModal(true);
    }
  }, [contactUsDetails]);

  const handleAddSubject = e => {
    setSubject(e);
  };

  const handleAddLocation = e => {
    setLocation(e);
  };

  const handleAddMessage = e => {
    setMessage(e);
  };

  const handleInfoClick = () => {
    setOpenInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setOpenInfoModal(false);
  };

  const handleGoToBackClick = () => {
    setOpenThanksModal(false);
    setOnContactUs(false);
    history.push('/');
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          const { email: optionalEmail, message, subject, location } = values;
          const { email, lastName } = userDetails;
          const data = {
            firstName: userFirstName,
            lastName,
            email: email,
            alternateEmail: optionalEmail,
            location,
            subject,
            message,
          };
          submitContact(data);
          setOnContactUs(true);
        }}
        validationSchema={contactUsSchema}
      >
        {({ handleSubmit, handleChange, values, errors, touched }) => {
          return (
            <form
              className={`${styles.body} ${isMobile ? styles.mob : null}`}
              onSubmit={handleSubmit}
            >
              {!isMobile ? <Text className={styles.heading}>Contact Us</Text> : ''}
              {!userDetails ? (
                <div className={styles.spinnerContainer}>
                  <CircularProgress size={45} style={{ color: '#fe5000' }} />
                </div>
              ) : (
                <div className={styles.outerContainer}>
                  <div className={styles.innerContainer}>
                    <div className={styles.card}>
                      <div className={styles.details}>
                        <div className={styles.detailsText}>
                          <p className={styles.key}>Full Name</p>
                          <p className={styles.value}>{userDetails?.name}</p>
                        </div>
                        <div className={styles.detailsText} style={{ marginTop: '10px' }}>
                          <p className={styles.key}>Phone Number</p>
                          <p className={styles.value}>{userPhoneNumber}</p>
                        </div>
                        <div className={styles.detailsText} style={{ marginTop: '10px' }}>
                          <p className={styles.key}>Email</p>
                          <p className={styles.value}>{userDetails?.email}</p>
                        </div>
                      </div>
                      <div>
                        <img
                          src={infoIcon}
                          alt="icon"
                          className={styles.logo}
                          onClick={handleInfoClick}
                        />
                      </div>
                    </div>
                    <div className={styles.fieldContainer}>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label}>
                          If the email shown above is incorrect,{isMobile ? <br /> : ''}{' '}
                          please provide a new email{' '}
                          <span className={styles.optional}>(optional)</span>.
                        </p>
                        <Textfield
                          value={values.email}
                          onChange={handleChange}
                          name="email"
                          id="email"
                          placeholder="i.e. john@gmail.com"
                          width={inputWidth}
                          className={''}
                          invalid={touched.email && Boolean(errors.email)}
                          error={errors.email}
                          boxShadow={boxShadow}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label}>Subject*</p>
                        <CustomDropdown
                          height={'50px'}
                          backgroundColor={'white'}
                          borderRadius={'10px'}
                          marginTop={'5px'}
                          placeholder="Please Select"
                          name="subject"
                          id="subject"
                          className={''}
                          width={inputWidth}
                          hideIndicatorSeparator
                          options={subjectOptions}
                          onChange={e => {
                            handleChange('subject')(e.value);
                            handleAddSubject(e.label);
                          }}
                          error={errors.subject}
                          invalid={touched.subject && Boolean(errors.subject)}
                          value={values.subject}
                          boxShadow={boxShadow}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label} style={{ marginTop: '15px' }}>
                          Location*
                        </p>
                        <CustomDropdown
                          height={'50px'}
                          backgroundColor={'white'}
                          borderRadius={'10px'}
                          marginTop={'5px'}
                          placeholder="Please Select"
                          name="location"
                          id="location"
                          className={''}
                          width={inputWidth}
                          hideIndicatorSeparator
                          options={locationOptions}
                          onChange={e => {
                            handleChange('location')(e.value);
                            handleAddLocation(e.label);
                          }}
                          error={errors.location}
                          invalid={touched.location && Boolean(errors.location)}
                          value={values.location}
                          boxShadow={boxShadow}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label} style={{ marginTop: '15px' }}>
                          Message*
                        </p>
                        <TextArea
                          value={values.message}
                          onChange={e => {
                            handleChange('message')(e.target.value);
                            handleAddMessage(e.target.value);
                          }}
                          name="message"
                          id="message"
                          rows={!isMobile ? 6 : 4}
                          width={inputWidth}
                          className={''}
                          invalid={touched.message && Boolean(errors.message)}
                          error={errors.message}
                          boxShadow={boxShadow}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={isMobile ? styles.buttonContainer : ''}>
                    <button
                      className={
                        !validData || isSubmitContactLoading
                          ? styles.disableSubmitButton
                          : styles.submitButton
                      }
                      disabled={!validData || isSubmitContactLoading}
                      type="submit"
                    >
                      {isSubmitContactLoading ? (
                        <CircularProgress
                          size={20}
                          style={{ color: '#fe5000', marginTop: '5px' }}
                        />
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </div>
              )}
              <Dialog
                open={openInfoModal}
                onClose={handleCloseInfoModal}
                classes={{ paper: classes.dialogPaper }}
                disableScrollLock
              >
                <Text className={styles.infoText}>
                  If you would like to change this info, please speak to one of our
                  Customer Service reps.
                </Text>
              </Dialog>
              <Dialog open={openThanksModal} classes={{ paper: classes.dialogPaper }}>
                <div className={styles.iconWrapper}>
                  <img
                    alt=""
                    src={checkmarkCircleOutline}
                    className={`${styles.checkLogo} ${isMobile ? styles.mob : ''}`}
                  />
                  <Text className={styles.thanksText1}>Thank you!</Text>
                  <Text className={styles.thanksText2}>
                    A representative will be in touch soon.
                  </Text>
                  <p onClick={handleGoToBackClick} className={styles.goBackText}>
                    Go Back to Home
                  </p>
                </div>
              </Dialog>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default ContactUs;
